import type { DraftValidationShape, PublicationReadiness, RubroRow, SkillOption } from './projectAutofillDraftUtils.ts'
import { mergeValidationWarnings } from './projectAutofillDraftUtils.ts'

export function normalizeWarningText(raw: string): string {
  return String(raw || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
}

export function warningMatchesSuggestedEndDate(w: string): boolean {
  const n = normalizeWarningText(w)
  return (
    n.includes('finaliz') &&
    (n.includes('suger') || n.includes('automatic') || n.includes('+1'))
  )
}

export function warningMatchesSuggestedDescription(w: string): boolean {
  const n = normalizeWarningText(w)
  return (
    n.includes('descripc') &&
    (n.includes('suger') || n.includes('automatic') || n.includes('documento'))
  )
}

export function warningMatchesSuggestedName(w: string): boolean {
  const n = normalizeWarningText(w)
  return (
    (n.includes('nombre') && (n.includes('suger') || n.includes('automatic'))) ||
    (n.includes('obra') && n.includes('suger') && n.includes('automatic'))
  )
}

export function warningMatchesSuggestedDocumentation(w: string): boolean {
  const n = normalizeWarningText(w)
  if (!n.includes('documentac')) return false
  return (
    n.includes('rubro') ||
    n.includes('planilla') ||
    n.includes('computo') ||
    n.includes('especificacion') ||
    n.includes('plano') ||
    n.includes('propuesta')
  )
}

export function isInlineSuggestionWarning(w: string): boolean {
  return (
    warningMatchesSuggestedEndDate(w) ||
    warningMatchesSuggestedDescription(w) ||
    warningMatchesSuggestedName(w) ||
    warningMatchesSuggestedDocumentation(w)
  )
}

export function isAdvisoryWarning(w: string): boolean {
  const n = normalizeWarningText(String(w || ''))
  if (!n.trim()) return false
  if (n.includes('vincul') && n.includes('palabras') && n.includes('comun')) return true
  if (n.includes('revisa') && n.includes('rubro') && n.includes('parecido')) return true
  if (n.includes('no sea otro') && n.includes('rubro')) return true
  return false
}

function collectReadinessProjectFieldPaths(pr: PublicationReadiness | undefined): Set<string> {
  const s = new Set<string>()
  if (!pr) return s
  const addKey = (k: string) => {
    if (k === 'project.name') s.add('project.name')
    else if (k === 'project.description') s.add('project.description')
    else if (k === 'project.startDate') s.add('project.startDate')
    else if (k === 'project.endDate') s.add('project.endDate')
    else if (k === 'project.zone') s.add('project.zone')
    else if (k === 'subprojects') s.add('subprojects')
    else if (k === 'subproject.selectedSkillId' || k === 'subproject.detectedLabel') s.add('subprojects')
    else if (k === 'dateConstraintOk') {
      s.add('project.startDate')
      s.add('project.endDate')
    }
  }
  for (const k of pr.missingForProjectAssign || []) addKey(k)
  for (const k of pr.missingForSubprojectCreate || []) addKey(k)
  if (pr.dateConstraintOk === false) {
    s.add('project.startDate')
    s.add('project.endDate')
  }
  return s
}

export function validationMessageToProjectFields(msg: string): string[] {
  const n = normalizeWarningText(msg)
  const out: string[] = []
  if (!n.trim()) return out
  if ((n.includes('nombre') && n.includes('proyecto')) || n.includes('falta el nombre')) out.push('project.name')
  if (
    n.includes('descripci') ||
    n.includes('descripcion') ||
    n.includes('falta la descripci') ||
    n.includes('falta la descripcion')
  ) {
    out.push('project.description')
  }
  if (
    n.includes('zona del proyecto') ||
    n.includes('ubicacion') ||
    (n.includes('ubicaci') && n.includes('obra'))
  ) {
    out.push('project.zone')
  }
  if (n.includes('fecha de inicio') || n.includes('falta fecha de inicio')) out.push('project.startDate')
  if (
    n.includes('fecha de finaliz') ||
    n.includes('falta fecha de finaliz') ||
    (n.includes('fecha') && n.includes('fin') && n.includes('proyecto'))
  ) {
    out.push('project.endDate')
  }
  if (n.includes('faltan fechas del proyecto')) {
    out.push('project.startDate', 'project.endDate')
  }
  if (n.includes('rubro') || n.includes('subproject')) out.push('subprojects')
  return out
}

export function matchValidationMessagesToRubroTempIds(
  messages: string[],
  rubros: RubroRow[],
  skillOptions: SkillOption[]
): Set<string> {
  const out = new Set<string>()
  if (rubros.length === 0) return out
  const keys = rubros.map((r, idx) => ({ r, key: r.tempId || `sp-${idx}` }))
  for (const raw of messages) {
    const msg = normalizeWarningText(String(raw || ''))
    if (!msg.trim()) continue
    if (!msg.includes('rubro') && !msg.includes('subproject')) continue
    for (const { r, key } of keys) {
      const label = (r.detectedLabel || '').trim().toLowerCase()
      if (label.length >= 2 && msg.includes(label)) {
        out.add(key)
        continue
      }
      const skill = skillOptions.find((s) => s.id === r.selectedSkillId)
      const sn = (skill?.name || '').trim().toLowerCase()
      if (sn.length >= 2 && msg.includes(sn)) {
        out.add(key)
        continue
      }
      const mn = (r.matchedSkillName || '').trim().toLowerCase()
      if (mn.length >= 2 && msg.includes(mn)) {
        out.add(key)
      }
    }
  }
  if (out.size === 0 && rubros.length === 1) {
    const anyRubroMsg = messages.some((raw) => {
      const msg = normalizeWarningText(String(raw || ''))
      return msg.includes('rubro') || msg.includes('subproject')
    })
    if (anyRubroMsg) out.add(keys[0].key)
  }
  return out
}

export function mergeProjectAutofillDraftValidation(params: {
  patched: DraftValidationShape | null | undefined
  payload: DraftValidationShape | null | undefined
  draft: DraftValidationShape | null | undefined
}): DraftValidationShape {
  const blockingIssues =
    params.patched?.blockingIssues ??
    params.payload?.blockingIssues ??
    params.draft?.blockingIssues ??
    []
  const warnings = mergeValidationWarnings(
    mergeValidationWarnings(params.payload?.warnings, params.draft?.warnings),
    params.patched?.warnings
  )
  const publicationReadiness =
    params.patched?.publicationReadiness ??
    params.draft?.publicationReadiness ??
    params.payload?.publicationReadiness
  return {
    blockingIssues: Array.isArray(blockingIssues) ? blockingIssues : [],
    warnings,
    publicationReadiness
  }
}

export type ProjectAutofillEditorUiState = {
  pendingFields: string[]
  pendingFieldsSet: Set<string>
  filteredBlockingIssues: string[]
  warningsNotHandledInline: string[]
  advisoryWarnings: string[]
  validationFieldPaths: Set<string>
  highlightedFields: Set<string>
}

export function computeProjectAutofillEditorUiState(params: {
  validation: DraftValidationShape
  requiredFields: string[]
  requiredFieldsWithZone: string[]
  draftProject?: {
    name?: string
    description?: string
    zone?: string
    startDate?: string
    endDate?: string
  }
  name: string
  description: string
  zone: string
  startDate: string
  endDate: string
  rubros: RubroRow[]
}): ProjectAutofillEditorUiState {
  const draftProject = params.draftProject ?? {}
  const valueByPath: Record<string, unknown> = {
    'project.name': params.name || draftProject.name,
    'project.description': params.description || draftProject.description,
    'project.zone': params.zone || draftProject.zone,
    'project.startDate': params.startDate || draftProject.startDate,
    'project.endDate': params.endDate || draftProject.endDate,
    subprojects: params.rubros
  }
  const pendingFields = params.requiredFieldsWithZone.filter((field) => {
    const value = valueByPath[field]
    if (Array.isArray(value)) return value.length === 0
    return !(value != null && String(value).trim() !== '')
  })
  const pendingFieldsSet = new Set(pendingFields)
  const requiresProjectName = params.requiredFields.includes('project.name')
  const requiresProjectDescription = params.requiredFields.includes('project.description')
  const requiresStartDate = params.requiredFields.includes('project.startDate')
  const requiresEndDate = params.requiredFields.includes('project.endDate')
  const requiresSubprojects = params.requiredFields.includes('subprojects')
  const zoneValuePresent = String(valueByPath['project.zone'] ?? '').trim() !== ''
  const zoneRequiredButEmpty =
    params.requiredFieldsWithZone.includes('project.zone') && !zoneValuePresent
  const blockingIssues = Array.isArray(params.validation.blockingIssues)
    ? params.validation.blockingIssues
    : []
  const filteredBlockingIssues = blockingIssues.filter((issue) => {
    const normalized = normalizeWarningText(String(issue || ''))
    if (!normalized) return false
    if (requiresProjectName && (normalized.includes('nombre del proyecto') || normalized.includes('falta el nombre')))
      return false
    if (
      requiresProjectDescription &&
      (normalized.includes('descripcion del proyecto') || normalized.includes('falta la descripcion'))
    ) {
      return false
    }
    if (requiresStartDate && (normalized.includes('fecha de inicio') || normalized.includes('falta fecha de inicio')))
      return false
    if (
      requiresEndDate &&
      (normalized.includes('fecha de finalizacion') || normalized.includes('falta fecha de finalizacion'))
    ) {
      return false
    }
    if ((requiresStartDate || requiresEndDate) && normalized.includes('faltan fechas del proyecto')) return false
    if (requiresSubprojects && (normalized.includes('rubros') || normalized.includes('subproject'))) return false
    if (
      zoneRequiredButEmpty &&
      (normalized.includes('zona del proyecto') || normalized.includes('ubicacion'))
    ) {
      return false
    }
    return true
  })
  const warnings = Array.isArray(params.validation.warnings) ? params.validation.warnings : []
  const advisoryWarnings = warnings.filter((w) => isAdvisoryWarning(String(w || '')))
  const warningsNotHandledInline = warnings.filter(
    (w) => !isInlineSuggestionWarning(String(w || '')) && !isAdvisoryWarning(String(w || ''))
  )
  const validationFieldPaths = collectReadinessProjectFieldPaths(params.validation.publicationReadiness)
  for (const issue of filteredBlockingIssues) {
    for (const p of validationMessageToProjectFields(String(issue))) {
      validationFieldPaths.add(p)
    }
  }
  for (const w of warningsNotHandledInline) {
    for (const p of validationMessageToProjectFields(String(w))) {
      validationFieldPaths.add(p)
    }
  }
  const highlightedFields = new Set<string>([...pendingFieldsSet, ...validationFieldPaths])
  return {
    pendingFields,
    pendingFieldsSet,
    filteredBlockingIssues,
    warningsNotHandledInline,
    advisoryWarnings,
    validationFieldPaths,
    highlightedFields
  }
}
